import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  DragIndicator,
  Edit,
  Sync,
  Visibility,
} from "@mui/icons-material";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  APPLY_MATCHING_RUN,
  GET_MATCHING_GROUP_CATEGORIES,
  GetMatchingGroupCategoriesResult,
  SAVE_MATCHING_OUTPUT,
  SYNC_MATCHING_TEAMS_TO_LMS,
} from "../../Queries";
import {
  MatchingOutputMember,
  MatchingOutputTeam,
  MatchingRun,
  MatchingRunStatus,
} from "../../types";

interface Props {
  open: boolean;
  run: MatchingRun;
  onClose: () => void;
  onApplied: (run: MatchingRun) => void;
}

type DraftTeam = {
  id: string;
  name: string;
  order: number;
  score: number | null;
  rationale: string;
  members: MatchingOutputMember[];
};

function cloneTeams(teams: MatchingOutputTeam[]): DraftTeam[] {
  return teams.map((t) => ({
    id: t.id,
    name: t.name,
    order: t.order,
    score: t.score,
    rationale: t.rationale,
    members: [...t.members],
  }));
}

/**
 * Returns the effective quality score for display.
 * null means the team is all non-responders → treated as 100%.
 */
function effectiveScore(score: number | null): number {
  return score ?? 1.0;
}

function teamQualityColor(
  score: number | null,
): "success" | "warning" | "default" {
  const s = effectiveScore(score);
  if (s >= 0.7) return "success";
  if (s >= 0.4) return "warning";
  return "default";
}

export function MatchingPreviewModal({ open, run, onClose, onApplied }: Props) {
  const [teams, setTeams] = useState<DraftTeam[]>(() =>
    cloneTeams(run.outputTeams),
  );
  // originalTeams tracks the pre-edit state so the "modified" chip is shown
  // only when the instructor has actually dragged/moved members.  It must be
  // reset alongside `teams` whenever the server data changes (e.g. after the
  // 10 s polling refetch delivers the initial results while the modal is
  // already mounted).  Using a plain setter — not a ref — keeps the memo
  // reactive so `isModified` and `hasChanges` stay accurate.
  const [originalTeams, setOriginalTeams] = useState<DraftTeam[]>(() =>
    cloneTeams(run.outputTeams),
  );
  const [editMode, setEditMode] = useState(false);

  // Drag-and-drop state
  const dragMemberId = useRef<string | null>(null);
  const dragFromTeamId = useRef<string | null>(null);
  const [dragOverTeamId, setDragOverTeamId] = useState<string | null>(null);

  // ── Apply-dialog state ──────────────────────────────────────────────────────
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyMode, setApplyMode] = useState<"new" | "existing">("new");
  const [newGroupSetName, setNewGroupSetName] = useState("Matching Teams");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  // Reset both draft teams AND the baseline snapshot when the server data
  // changes.  Without resetting originalTeams here, a polling refetch that
  // delivers outputTeams after the component mounts would cause every team
  // to show a spurious "modified" chip even though the instructor did nothing.
  React.useEffect(() => {
    const cloned = cloneTeams(run.outputTeams);
    setTeams(cloned);
    setOriginalTeams(cloned);
    setEditMode(false);
    setDragOverTeamId(null);
  }, [run.outputTeams]);

  const [
    fetchCategories,
    { data: categoriesData, loading: categoriesLoading },
  ] = useLazyQuery<GetMatchingGroupCategoriesResult>(
    GET_MATCHING_GROUP_CATEGORIES,
  );

  const [saveOutput, { loading: saving }] = useMutation(SAVE_MATCHING_OUTPUT, {
    refetchQueries: ["GetMatchingRun", "GetMatchingRuns"],
  });

  const [applyRun, { loading: applying }] = useMutation(APPLY_MATCHING_RUN, {
    refetchQueries: ["GetMatchingRun", "GetMatchingRuns"],
    onCompleted: (data) => {
      const updated = data.applyMatchingRun;
      if (updated?.id) onApplied(updated);
    },
  });

  const [syncToLms, { loading: syncing }] = useMutation(
    SYNC_MATCHING_TEAMS_TO_LMS,
    {
      refetchQueries: ["GetMatchingRun", "GetMatchingRuns"],
      onCompleted: (data) => {
        const updated = data.syncMatchingTeamsToLms;
        if (updated?.id) onApplied(updated);
      },
    },
  );

  const isApplied =
    run.status === MatchingRunStatus.Applied ||
    run.status === MatchingRunStatus.LmsSynced;
  const isReady = run.status === MatchingRunStatus.Ready;

  // Check if teams have been modified from original
  const isModified = React.useMemo(() => {
    if (teams.length !== originalTeams.length) return true;
    for (let i = 0; i < teams.length; i++) {
      const t = teams[i];
      const orig = originalTeams.find((o) => o.id === t.id);
      if (!orig) return true;
      if (t.members.length !== orig.members.length) return true;
      const tIds = new Set(t.members.map((m) => m.id));
      if (orig.members.some((m) => !tIds.has(m.id))) return true;
    }
    return false;
  }, [teams, originalTeams]);

  // ── Drag-and-drop handlers ──────────────────────────────────────────────────

  function handleMemberDragStart(
    e: React.DragEvent,
    memberId: string,
    fromTeamId: string,
  ) {
    dragMemberId.current = memberId;
    dragFromTeamId.current = fromTeamId;
    e.dataTransfer.effectAllowed = "move";
  }

  function handleTeamDragOver(e: React.DragEvent, toTeamId: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTeamId(toTeamId);
  }

  function handleTeamDrop(e: React.DragEvent, toTeamId: string) {
    e.preventDefault();
    const memberId = dragMemberId.current;
    const fromTeamId = dragFromTeamId.current;
    dragMemberId.current = null;
    dragFromTeamId.current = null;
    setDragOverTeamId(null);

    if (!memberId || !fromTeamId || fromTeamId === toTeamId) return;

    setTeams((prev) => {
      const updated = prev.map((t) => ({ ...t, members: [...t.members] }));
      const fromTeam = updated.find((t) => t.id === fromTeamId);
      const toTeam = updated.find((t) => t.id === toTeamId);
      if (!fromTeam || !toTeam) return prev;
      const memberIdx = fromTeam.members.findIndex((m) => m.id === memberId);
      if (memberIdx === -1) return prev;
      const [member] = fromTeam.members.splice(memberIdx, 1);
      toTeam.members.push(member);
      return updated;
    });
  }

  function handleDragEnd() {
    dragMemberId.current = null;
    dragFromTeamId.current = null;
    setDragOverTeamId(null);
  }

  // ── Save / Apply handlers ──────────────────────────────────────────────────

  async function handleSave() {
    await saveOutput({
      variables: {
        runId: run.id,
        teams: teams.map((t, i) => ({
          name: t.name,
          order: i,
          members: t.members.map((m, j) => ({
            userId: m.userId,
            order: j,
          })),
        })),
      },
    });
    setToastMsg("Team adjustments saved.");
    setToastOpen(true);
    setEditMode(false);
  }

  function handleApply() {
    setApplyMode("new");
    setNewGroupSetName("Matching Teams");
    setSelectedCategoryId(null);
    fetchCategories();
    setApplyDialogOpen(true);
  }

  async function handleConfirmApply() {
    await applyRun({
      variables: {
        runId: run.id,
        groupCategoryId: applyMode === "existing" ? selectedCategoryId : null,
        newGroupSetName: applyMode === "new" ? newGroupSetName : null,
      },
    });
    setApplyDialogOpen(false);
  }

  async function handleSyncToLms() {
    await syncToLms({ variables: { runId: run.id } });
  }

  const confirmApplyDisabled =
    applying ||
    (applyMode === "new" && !newGroupSetName.trim()) ||
    (applyMode === "existing" && !selectedCategoryId);

  const categories = categoriesData?.matchingGroupCategories ?? [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <span>
              {isApplied ? "Applied Teams" : "Review & Customize Teams"}
            </span>
            <Box display="flex" alignItems="center" gap={1}>
              {run.score != null && (
                <Chip
                  label={`Match quality: ${(run.score * 100).toFixed(1)}%`}
                  color="primary"
                  size="small"
                />
              )}
              {isReady && (
                <Tooltip
                  title={
                    editMode
                      ? "Switch to view mode"
                      : "Edit mode — drag members between teams"
                  }
                >
                  <IconButton
                    size="small"
                    color={editMode ? "primary" : "default"}
                    aria-label={editMode ? "view mode" : "edit mode"}
                    onClick={() => setEditMode((v) => !v)}
                  >
                    {editMode ? (
                      <Visibility fontSize="small" />
                    ) : (
                      <Edit fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {editMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Edit mode:</strong> Drag members between team boxes to
              reassign them. Save your changes before applying.
              {isModified && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1, color: "warning.main", fontWeight: 600 }}
                >
                  (unsaved changes)
                </Typography>
              )}
            </Alert>
          )}

          {!isApplied && isReady && !editMode && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Review the proposed teams below. Click the{" "}
              <Edit sx={{ fontSize: 14, verticalAlign: "middle" }} /> edit
              button to drag members between teams. When ready, click{" "}
              <strong>Apply Teams</strong> to create groups in the course.
            </Typography>
          )}

          {/* Teams sorted by quality: lowest score first so problem teams are immediately visible */}
          <Stack spacing={2}>
            {[...teams]
              .sort((a, b) => effectiveScore(a.score) - effectiveScore(b.score))
              .map((team) => {
                const origTeam = originalTeams.find((o) => o.id === team.id);
                const origMemberIds = new Set(
                  origTeam?.members.map((m) => m.id) ?? [],
                );
                const hasChanges =
                  isModified &&
                  (team.members.length !== (origTeam?.members.length ?? 0) ||
                    team.members.some((m) => !origMemberIds.has(m.id)));

                return (
                  <Box
                    key={team.id}
                    onDragOver={
                      editMode
                        ? (e) => handleTeamDragOver(e, team.id)
                        : undefined
                    }
                    onDrop={
                      editMode ? (e) => handleTeamDrop(e, team.id) : undefined
                    }
                    onDragLeave={
                      editMode ? () => setDragOverTeamId(null) : undefined
                    }
                    sx={{
                      border: "1px solid",
                      borderColor:
                        dragOverTeamId === team.id ? "primary.main" : "divider",
                      borderRadius: 1,
                      p: 2,
                      transition: "border-color 0.15s",
                      bgcolor:
                        dragOverTeamId === team.id
                          ? "action.hover"
                          : "background.paper",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {team.name}
                        </Typography>
                        {hasChanges && (
                          <Chip
                            label="modified"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Chip
                          label={`${(effectiveScore(team.score) * 100).toFixed(
                            0,
                          )}%`}
                          size="small"
                          color={teamQualityColor(team.score)}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {team.members.length} member
                          {team.members.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Box>

                    {team.rationale && (
                      <Typography
                        variant="caption"
                        color={
                          effectiveScore(team.score) < 1.0
                            ? "warning.main"
                            : "text.secondary"
                        }
                        display="block"
                        mb={1}
                      >
                        ⚠ {team.rationale}
                      </Typography>
                    )}

                    <Divider sx={{ mb: 1 }} />
                    <List dense disablePadding>
                      {team.members.map((member) => (
                        <ListItem
                          key={member.id}
                          disablePadding
                          sx={{
                            cursor: editMode ? "grab" : "default",
                            "&:active": editMode
                              ? { cursor: "grabbing" }
                              : undefined,
                            borderRadius: 0.5,
                            px: editMode ? 0.5 : 0,
                            "&:hover": editMode
                              ? { bgcolor: "action.hover" }
                              : undefined,
                          }}
                          draggable={editMode}
                          onDragStart={
                            editMode
                              ? (e) =>
                                  handleMemberDragStart(e, member.id, team.id)
                              : undefined
                          }
                          onDragEnd={editMode ? handleDragEnd : undefined}
                        >
                          {editMode && (
                            <DragIndicator
                              fontSize="small"
                              sx={{ color: "text.disabled", mr: 0.5 }}
                            />
                          )}
                          <ListItemText
                            primary={member.name}
                            secondary={member.email}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>

          {isReady && editMode && (
            <Button onClick={handleSave} disabled={saving} variant="outlined">
              {saving ? "Saving…" : "Save Adjustments"}
            </Button>
          )}

          {isReady && !editMode && (
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleApply}
              disabled={applying}
            >
              Apply Teams
            </Button>
          )}

          {(run.status === MatchingRunStatus.Applied ||
            run.status === MatchingRunStatus.LmsSynced) && (
            <Button
              variant="contained"
              startIcon={<Sync />}
              onClick={handleSyncToLms}
              disabled={syncing}
            >
              {syncing ? "Syncing…" : "Sync to LMS"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Group-set confirmation dialog ─────────────────────────────── */}
      <Dialog
        open={applyDialogOpen}
        onClose={() => setApplyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="apply-dialog-title"
      >
        <DialogTitle id="apply-dialog-title">Apply Teams</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            How should teams be created in the course?
          </Typography>

          <RadioGroup
            value={applyMode}
            onChange={(e) => setApplyMode(e.target.value as "new" | "existing")}
          >
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Create new group set"
            />

            {applyMode === "new" && (
              <TextField
                label="Group set name"
                value={newGroupSetName}
                onChange={(e) => setNewGroupSetName(e.target.value)}
                size="small"
                fullWidth
                sx={{ ml: 4, mb: 1, width: "calc(100% - 32px)" }}
                inputProps={{ "aria-label": "group set name" }}
              />
            )}

            <FormControlLabel
              value="existing"
              control={<Radio />}
              label="Replace existing group set"
            />

            {applyMode === "existing" && (
              <FormControl
                size="small"
                fullWidth
                sx={{ ml: 4, width: "calc(100% - 32px)" }}
              >
                <InputLabel id="category-select-label">Group set</InputLabel>
                <Select
                  labelId="category-select-label"
                  label="Group set"
                  value={selectedCategoryId ?? ""}
                  onChange={(e) =>
                    setSelectedCategoryId(e.target.value as string)
                  }
                  inputProps={{ "aria-label": "existing group set" }}
                  disabled={categoriesLoading}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                  {categories.length === 0 && !categoriesLoading && (
                    <MenuItem disabled value="">
                      No group sets found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Back</Button>
          <Button
            variant="contained"
            startIcon={<Check />}
            onClick={handleConfirmApply}
            disabled={confirmApplyDisabled}
            aria-label="confirm apply teams"
          >
            {applying ? "Applying…" : "Apply Teams"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast for save feedback */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message={toastMsg}
      />
    </>
  );
}
